import { CONFIG } from '../data/config';
import { personalization } from '../data/personalization';
import { newClock, type GameClock } from '../sim/time';
import type { PlantedCrop } from '../sim/crops';
import type { ShippingEntry } from '../sim/economy';
import type { TileXY } from '../types';

/** Bump when the GameState shape changes, and add a migration in sim/migrate.ts. */
export const SAVE_VERSION = 17;

export interface InventorySlot {
  itemId: string;
  qty: number;
}

export interface PlacedDecoration {
  decoId: string;
  x: number;
  y: number;
  /** Growth progress (days) for growable decorations like saplings. */
  growth?: number;
}

export interface Animal {
  id: string;
  type: string;
  x: number;
  y: number;
  /** Last day this animal produced (lays once per morning). */
  lastEggDay: number;
  /** Happiness from daily care (0–10) — happy animals sometimes give extra. */
  happy?: number;
}

export interface GameStats {
  daysPlayed: number;
  totalEarned: number;
  cropsHarvested: number;
}

/** The single serializable source of truth. Everything persists through this. */
export interface GameState {
  saveVersion: number;
  playerName: string;
  farmName: string;
  money: number;
  energy: number;
  energyMax: number;
  clock: GameClock;
  /** Player tile position (where they last stood / wake). */
  player: TileXY;
  /** Hoed soil tiles (may or may not currently hold a crop). */
  tilled: TileXY[];
  /** Weeds to clear (Tier 2). */
  weeds: TileXY[];
  /** Tiles whose tree was chopped — re-hidden on load so they stay gone. */
  chopped: TileXY[];
  crops: PlantedCrop[];
  inventory: InventorySlot[];
  /** Chest storage inside the house. */
  storage: InventorySlot[];
  decorations: PlacedDecoration[];
  /** Decorations placed inside the house. */
  interiorDecor: PlacedDecoration[];
  /** Livestock + uncollected eggs (Tier 2). */
  animals: Animal[];
  eggs: TileXY[];
  shippingBin: ShippingEntry[];
  /** Misc boolean flags — a seam for future content (no logic depends on this in T1). */
  flags: Record<string, boolean>;
  /** Today's weather (set each morning). */
  weather: 'clear' | 'rain';
  /** Goal ids whose reward has been claimed. */
  goalsClaimed: string[];
  /** Pending mailbox gift (gift = item id, or 'gold'), or null. */
  mail: { gift: string; qty: number } | null;
  /** Friendship points with Clara (each gift +1). */
  claraHearts: number;
  /** Build-mode position overrides for movable buildings (empty = default layout). */
  buildings: Record<string, { x: number; y: number }>;
  /** The pet: a cat or a dog (chosen at new farm). */
  petType: 'cat' | 'dog';
  /** Custom pet name (empty = a default). */
  petName: string;
  /** Bond with the pet (each pet +1). */
  petHearts: number;
  /** A keepsake diary of milestones (first occurrence of each). */
  journal: { day: number; text: string }[];
  /** House upgrade tier (index into HOUSE_TIERS). */
  houseLevel: number;
  /** Barn upgrade tier (0 = basic) — raises animal capacity + happiness. */
  barnLevel: number;
  /** Collection log: total of each item ever obtained. */
  collected: Record<string, number>;
  stats: GameStats;
}

export function createInitialState(): GameState {
  return {
    saveVersion: SAVE_VERSION,
    playerName: personalization.playerDefaultName,
    farmName: personalization.farmName,
    money: CONFIG.startingMoney,
    energy: CONFIG.energyMax,
    energyMax: CONFIG.energyMax,
    clock: newClock(),
    player: { x: 9, y: 13 },
    tilled: [],
    weeds: [],
    chopped: [],
    crops: [],
    inventory: starterInventory(),
    storage: [],
    decorations: [],
    interiorDecor: [],
    animals: [],
    eggs: [],
    shippingBin: [],
    flags: {},
    weather: 'clear',
    goalsClaimed: [],
    mail: null,
    claraHearts: 0,
    buildings: {},
    petType: 'cat',
    petName: '',
    petHearts: 0,
    journal: [],
    houseLevel: 0,
    barnLevel: 0,
    collected: {},
    stats: { daysPlayed: 1, totalEarned: 0, cropsHarvested: 0 },
  };
}

/** Start with a few seeds and the placeable decorations (no shop in Tier 1). */
function starterInventory(): InventorySlot[] {
  return [
    { itemId: 'seed_turnip', qty: 12 },
    { itemId: 'seed_potato', qty: 8 },
    { itemId: 'seed_carrot', qty: 8 },
    { itemId: 'flowerpot', qty: 2 },
    { itemId: 'lantern', qty: 2 },
    { itemId: 'bench', qty: 1 },
    { itemId: 'scarecrow', qty: 1 },
    { itemId: 'rug', qty: 1 },
  ];
}
